import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useToastState } from '../../store/useAppStore';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

// Hook for toast notifications (can't use hooks in class components)
const ErrorBoundaryWithToast: React.FC<ErrorBoundaryProps> = ({ children, fallback, onError }) => {
  const { showToast } = useToastState();

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    showToast({
      type: 'error',
      title: 'Erro Inesperado',
      message: 'Ocorreu um erro inesperado. A página será recarregada automaticamente.',
      duration: 0 // Não desaparecer automaticamente
    });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  };

  return (
    <ErrorBoundaryClass onError={handleError} fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  );
};

class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps & { onError?: (error: Error, errorInfo: ErrorInfo) => void },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Call the error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReportError = () => {
    const { error, errorInfo } = this.state;
    const errorReport = {
      error: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Copy error report to clipboard
    navigator.clipboard.writeText(JSON.stringify(errorReport, null, 2))
      .then(() => {
        alert('Relatório de erro copiado para a área de transferência!');
      })
      .catch(() => {
        console.log('Error report:', errorReport);
        alert('Falha ao copiar. Verifique o console para o relatório completo.');
      });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          margin: '2rem',
          border: '2px solid #e2e8f0'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1rem'
          }}>
            😵
          </div>
          
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '0.5rem'
          }}>
            Oops! Algo deu errado
          </h2>
          
          <p style={{
            color: '#6b7280',
            marginBottom: '2rem',
            maxWidth: '400px'
          }}>
            Ocorreu um erro inesperado na aplicação. Você pode tentar recarregar a página ou reportar o problema.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <button
              onClick={this.handleReload}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.875rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              🔄 Recarregar Página
            </button>

            <button
              onClick={this.handleReset}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.875rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              🔙 Tentar Novamente
            </button>

            <button
              onClick={this.handleReportError}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.875rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              📋 Reportar Erro
            </button>
          </div>

          {/* Development error details */}
          {(import.meta as any).env.DEV && (
            <details style={{
              marginTop: '2rem',
              padding: '1rem',
              backgroundColor: '#fee2e2',
              borderRadius: '8px',
              border: '1px solid #fecaca',
              maxWidth: '100%',
              textAlign: 'left'
            }}>
              <summary style={{
                cursor: 'pointer',
                fontWeight: '600',
                color: '#dc2626',
                marginBottom: '0.5rem'
              }}>
                Detalhes do Erro (Desenvolvimento)
              </summary>
              
              <div style={{
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                <strong>Error:</strong><br />
                {this.state.error?.toString()}
                
                <br /><br />
                <strong>Component Stack:</strong><br />
                {this.state.errorInfo?.componentStack}
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryWithToast;